import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { PageHeader } from "../../components/admin/PageHeader";
import { ClientPicker } from "../../components/admin/ClientPicker";
import { ShareHistory } from "../../components/admin/ShareHistory";
import { ProfileListPanel, PROFILE_COLUMNS } from "../../components/admin/ProfileListPanel";
import { useAuth } from "../../context/AuthContext";
import { setFeatured } from "../../lib/adminApi";
import { supabase } from "../../lib/supabase";

export default function MatchesShares() {
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [showFor, setShowFor] = useState("all");
  const [sharedIds, setSharedIds] = useState(new Set());
  const [reloadKey, setReloadKey] = useState(0);
  const bump = () => setReloadKey((k) => k + 1);

  const loadShared = useCallback(async () => {
    if (!client) return;
    const { data } = await supabase.from("shared_profiles").select("shared_profile_id").eq("client_profile_id", client.id).eq("is_revoked", false);
    setSharedIds(new Set((data || []).map((r) => r.shared_profile_id)));
  }, [client]);
  useEffect(() => { loadShared(); }, [loadShared, reloadKey]);

  const share = async (ids) => {
    const now = new Date().toISOString();
    const rows = ids.map((sid) => ({ client_profile_id: client.id, shared_profile_id: sid, shared_by: user.id, shared_at: now, is_revoked: false, revoked_at: null, response: "none", responded_at: null }));
    const { error } = await supabase.from("shared_profiles").upsert(rows, { onConflict: "client_profile_id,shared_profile_id" });
    if (error) throw error;
    toast.success(`${ids.length} profile(s) shared with ${client.full_name}`);
    bump();
  };
  const shareOne = (ids) => share(ids).catch((e) => toast.error(e.message));

  const toggleFeatured = async (r, reload) => {
    try { await setFeatured(r.id, !r.is_featured); reload(); } catch (e) { toast.error(e.message); }
  };

  const sharedCol = { key: "shared", label: "Shared", render: (r) => (sharedIds.has(r.id) ? <span data-testid={`shared-flag-${r.id}`} className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-800">Shared</span> : "-") };
  const columns = [...PROFILE_COLUMNS.filter((c) => !["status", "phone"].includes(c.key)), sharedCol];
  const bulk = [{
    id: "share", label: "Share selected",
    confirm: (n) => ({ title: `Share ${n} profile(s) with ${client?.full_name}?`, description: "The client will be able to see these profiles in their private portal." }),
    run: (ids) => share(ids),
  }];

  return (
    <div data-testid="matches-page">
      <PageHeader eyebrow="Matchmaking" title="Matches & Shares" />
      <ClientPicker client={client} onPick={(c) => { setClient(c); setSharedIds(new Set()); setShowFor(c ? (c.profile_for === "bride" ? "groom" : "bride") : "all"); }} />
      {client && (
        <Tabs defaultValue="browse" className="mt-6">
          <TabsList>
            <TabsTrigger value="browse" data-testid="tab-browse">Browse approved profiles</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Share history &amp; responses</TabsTrigger>
          </TabsList>
          <TabsContent value="browse" className="mt-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-[#2C2424]">
              Show:
              <select value={showFor} onChange={(e) => setShowFor(e.target.value)} data-testid="show-for-filter" className="rmm-select rmm-input h-9 w-36">
                <option value="bride">Brides</option>
                <option value="groom">Grooms</option>
                <option value="all">All</option>
              </select>
            </div>
            <ProfileListPanel
              key={`${client.id}-${showFor}`}
              base={{ status: "approved", excludeId: client.id, profile_for: showFor !== "all" ? showFor : undefined }}
              hideStatus
              columns={columns}
              bulkActions={bulk}
              reloadKey={reloadKey}
              rowActions={(r, reload) => (
                <span className="inline-flex gap-3">
                  <button type="button" onClick={() => shareOne([r.id])} data-testid={`share-${r.id}`} className="text-xs font-semibold text-[#7A0016] hover:underline">{sharedIds.has(r.id) ? "Re-share" : "Share"}</button>
                  <button type="button" onClick={() => toggleFeatured(r, reload)} data-testid={`feature-${r.id}`} className="text-xs font-semibold text-[#C59B27] hover:text-[#7A0016]">{r.is_featured ? "Unfeature" : "Feature"}</button>
                </span>
              )}
              exportName="match-candidates"
              exportTitle={`Match candidates for ${client.full_name}`}
            />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <ShareHistory clientId={client.id} reloadKey={reloadKey} onChanged={bump} share={shareOne} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
